// src/utils/GuestMapper.ts
import {
  AccompanyGuest,
  Guest,
  GuestFormDataWithCompletion,
  GuestKind,
  Reservation,
  SharedGuest,
  UnsyncedCreateGuestDto,
} from "../models/StaticDataModel";
import { mapRemoteGuestProfilePayloadToForm } from "../api/GuestService";
import {
  deriveKindFromDob,
  parseReservationArrivalDate,
} from "./guestSlotAgeValidation";
import { getChildMaxAgeYears } from "../config/childMaxAge";
import { getEmptyGuest, hasAllMandatoryData, isEmptyGuest } from "./guestUtils";

type ResolvedGuest = GuestFormDataWithCompletion & { guestKind: GuestKind };

/**
 * Builds a {@link GuestFormDataWithCompletion} from a PENDING/FAILED CREATE submission row.
 * Uses the persisted {@code guestPayload} preview so the card already shows the typed name even
 * before Opera has any record of the guest.
 */
function mapUnsyncedCreateToGuestForm(u: UnsyncedCreateGuestDto): GuestFormDataWithCompletion {
  const syntheticId = -Math.abs(u.submissionId);
  const preview = u.previewGuestData;
  const mapped = preview
    ? mapRemoteGuestProfilePayloadToForm(preview, syntheticId)
    : { ...getEmptyGuest(), id: syntheticId };
  const withMeta: GuestFormDataWithCompletion = {
    ...mapped,
    id: syntheticId,
    checkInSubmissionId: u.submissionId,
    syncStatus: u.syncStatus,
    syncError: u.syncError ?? undefined,
    updateUser: false,
  };
  const completed = hasAllMandatoryData(withMeta, undefined, { requireContact: false });
  return { ...withMeta, completed };
}

/**
 * Turns an Opera-returned share/accompany/main profile into the form shape used by the cards.
 * Pre-existing Opera guests carry minimal fields here; the page later hydrates them via
 * fetchGuestById when the user opens a card.
 */
function mapOperaGuestToForm(
  g: Guest | SharedGuest | AccompanyGuest
): GuestFormDataWithCompletion {
  const base: GuestFormDataWithCompletion = {
    ...getEmptyGuest(),
    id: Number(String(g.id)),
    firstName: g.firstName ?? "",
    lastName: g.lastName ?? "",
    completed: false,
  };
  const allowUpdate = !isEmptyGuest(base);
  return { ...base, updateUser: allowUpdate };
}

function makePlaceholder(index: number, kind: GuestKind): ResolvedGuest {
  return {
    ...getEmptyGuest(),
    id: Date.now() + index,
    firstName: "",
    lastName: "",
    completed: false,
    updateUser: false,
    guestKind: kind,
  };
}

/**
 * Resolves a single Opera or unsynced guest into a kind, then a form row. Precedence:
 *   1. Persisted kind (from {@code guestKindAssignments} for synced guests or the unsynced row).
 *   2. DOB-derived kind (only when DOB is exposed on the listing DTO, currently SharedGuest).
 *   3. Default ADULT.
 * The {@code guestKind} field is mirrored onto the returned row so the form can pick the right
 * DOB validation bounds even for overflow guests rendered outside the canonical layout.
 */
function resolveOperaSharerKind(
  g: SharedGuest | AccompanyGuest,
  reservation: Reservation
): GuestKind {
  const assigned = reservation.guestKindAssignments?.[String(g.id)];
  if (assigned === "ADULT" || assigned === "CHILD") return assigned;
  const arrival = parseReservationArrivalDate(reservation.arrivalDate);
  const dob = "dateOfBirth" in g ? g.dateOfBirth ?? null : null;
  const derived = deriveKindFromDob(dob, arrival, getChildMaxAgeYears());
  return derived ?? "ADULT";
}

function resolveUnsyncedKind(
  u: UnsyncedCreateGuestDto,
  reservation: Reservation
): GuestKind {
  if (u.guestKind === "ADULT" || u.guestKind === "CHILD") return u.guestKind;
  // Legacy rows (no persisted kind): try the previewed DOB on the unsynced payload as a tiebreaker.
  const arrival = parseReservationArrivalDate(reservation.arrivalDate);
  const dob = (u.previewGuestData as { dateOfBirth?: { day?: number | null; month?: number | null; year?: number | null } } | undefined)?.dateOfBirth;
  const derived = deriveKindFromDob(dob, arrival, getChildMaxAgeYears());
  return derived ?? "ADULT";
}

/**
 * Builds the public UI's {@code guests[]} from the reservation listing plus our DB's pending /
 * synced CREATE submissions. The output is laid out so:
 *   - Slot 0 is always the main guest (the booker).
 *   - Slots 1..adultCount-1 are adults (bucketed by kind, NOT by Opera order within the bucket).
 *   - Slots adultCount..total-1 are children.
 *   - Any guest whose kind has no room left is appended as "overflow" so the user can still see
 *     and edit them — this happens when reservation counts shrink below the number of guests
 *     already filled in.
 *
 * Kind precedence: persisted (guestKindAssignments / unsynced.guestKind) -> DOB-derived -> ADULT.
 */
export const mapGuestsFromReservation = async (
  reservation: Reservation
): Promise<GuestFormDataWithCompletion[]> => {
  const adultCount = Math.max(0, reservation.adultCount ?? 0);
  const childCount = Math.max(0, reservation.childCount ?? 0);

  const mainGuest = reservation.mainGuest;
  const sharers: (SharedGuest | AccompanyGuest)[] = [
    ...(reservation.sharedGuests ?? []),
    ...(reservation.accompanyGuests ?? []),
  ];

  // Skip any sharer whose Opera profile id equals the main guest's id — the ReservationMapper
  // already dedupes this server-side, but the listing endpoint can in principle list the same
  // profile twice in older payloads. Filtering here keeps the bucketing idempotent.
  const mainGuestId = mainGuest?.id != null ? String(mainGuest.id) : null;
  const sharersDeduped = mainGuestId
    ? sharers.filter((g) => String(g.id) !== mainGuestId)
    : sharers;

  // Bucket synced + unsynced rows into ADULT / CHILD lists, preserving order: Opera order first,
  // then our DB createdAt order for unsynced rows. (Within a kind, ordering is intentionally not
  // preserved across refreshes per product call — only kind matters.)
  const adultRows: ResolvedGuest[] = [];
  const childRows: ResolvedGuest[] = [];

  // Main guest -> always adult slot 0 (booker convention; no DOB / kind check).
  if (mainGuest && mainGuest.id != null && String(mainGuest.id).trim() !== "") {
    const row = mapOperaGuestToForm(mainGuest);
    adultRows.push({ ...row, guestKind: "ADULT" });
  }

  for (const g of sharersDeduped) {
    const kind = resolveOperaSharerKind(g, reservation);
    const row = mapOperaGuestToForm(g);
    const withKind: ResolvedGuest = { ...row, guestKind: kind };
    (kind === "CHILD" ? childRows : adultRows).push(withKind);
  }

  for (const u of reservation.unsyncedCreateGuests ?? []) {
    const kind = resolveUnsyncedKind(u, reservation);
    const row = mapUnsyncedCreateToGuestForm(u);
    const withKind: ResolvedGuest = { ...row, guestKind: kind };
    (kind === "CHILD" ? childRows : adultRows).push(withKind);
  }

  // Lay out the canonical adult/child slots; anything that doesn't fit becomes overflow so the
  // guest stays visible/editable. We never silently drop a guest because Opera has them attached
  // to the reservation either way (or we have a pending row in our DB).
  const adultsFit = adultRows.slice(0, adultCount);
  const adultsOverflow = adultRows.slice(adultCount);
  const childrenFit = childRows.slice(0, childCount);
  const childrenOverflow = childRows.slice(childCount);

  const out: GuestFormDataWithCompletion[] = [];

  // Adult slots: 0..adultCount-1. Pad with placeholders so the layout always has exactly N cards.
  for (let i = 0; i < adultCount; i++) {
    out.push(adultsFit[i] ?? makePlaceholder(i, "ADULT"));
  }
  // Child slots: adultCount..total-1.
  for (let i = 0; i < childCount; i++) {
    out.push(childrenFit[i] ?? makePlaceholder(adultCount + i, "CHILD"));
  }
  // Edge case: a reservation with 0 adults + 0 children still gets one slot so the page doesn't
  // render empty. Falls back to a single adult placeholder.
  if (out.length === 0) {
    out.push(makePlaceholder(0, "ADULT"));
  }

  // Append overflow guests with overflow=true so the page can group them separately.
  for (const row of [...adultsOverflow, ...childrenOverflow]) {
    out.push({ ...row, overflow: true });
  }

  return out;
};
